<script setup lang="ts">
import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import { SellingMethod, AssetType } from '~/apiClient';
import type { Breadcrumb } from '~/types/breadcrumb.type';

// COMPOSABLES
const { tt } = useTypedI18n();
const route = useRoute();
const sellingMethod = useSlpSellingMethod();
const parsedRouteParams = useSlpParsedRouteParamsState(route);
const { brandName, modelName, singleSelectedBrandUrlKey, selectedModelUrlKey } = useSlpParams(sellingMethod);
const { formatSlpRoute } = useSlpRouteUtils();

// DATA
const title = computed<string>(() => {
    const titleParams = [
        parsedRouteParams.value.platformClass ? tt(`asset.class.${parsedRouteParams.value.platformClass}`) : parsedRouteParams.value.platformClass,
        brandName.value,
        modelName.value,
    ].filter(Boolean);

    return titleParams.every(value => !value)
        ? tt(`slp.${sellingMethod.value}.page.title.base`)
        : tt(`slp.${sellingMethod.value}.page.title.filtered`, {
              assetType: tt(`asset.type.${parsedRouteParams.value.assetType!}`),
              filters: titleParams.join(' '),
          });
});

const breadCrumbs = computed<Breadcrumb[]>(() => [
    {
        label: tt('homepage.link'),
        to: STORYBLOK_ROUTES.HOMEPAGE,
    },
    {
        label: tt(`asset.type.${AssetType.Bus}`, 2),
        to: formatSlpRoute({ sellingMethod: sellingMethod.value, assetType: AssetType.Bus }),
    },
    ...insertIf(!!parsedRouteParams.value.platformClass, () => ({
        label: tt(`asset.class.${parsedRouteParams.value.platformClass!}`),
        to: formatSlpRoute({
            sellingMethod: sellingMethod.value,
            assetType: AssetType.Bus,
            platformClass: parsedRouteParams.value.platformClass,
        }),
    })),
    ...insertIf(!!brandName.value, {
        label: brandName.value!,
        to: formatSlpRoute({
            ...setIf(!!parsedRouteParams.value.platformClass, { platformClass: parsedRouteParams.value.platformClass }),
            sellingMethod: sellingMethod.value,
            assetType: AssetType.Bus,
            brandUrlKey: singleSelectedBrandUrlKey.value,
        }),
    }),
    ...insertIf(!!modelName.value, {
        label: modelName.value!,
        to: formatSlpRoute({
            assetType: AssetType.Bus,
            ...setIf(!!parsedRouteParams.value.platformClass, { platformClass: parsedRouteParams.value.platformClass }),
            sellingMethod: sellingMethod.value,
            brandUrlKey: singleSelectedBrandUrlKey.value,
            modelUrlKey: selectedModelUrlKey.value,
        }),
    }),
]);

const icon = computed<string>(() => (sellingMethod.value === SellingMethod.Auction ? 'lucide:gavel' : 'lucide:shopping-bag'));
</script>

<template>
    <section class="overflow-x-hidden">
        <BaseWidthContainer class="flex flex-col gap-6 py-6">
            <BaseBreadcrumb :items="breadCrumbs" />
            <div class="flex items-center gap-3 text-2xl md:text-4xl lg:text-5xl">
                <div class="bg-primary-500 flex shrink-0 items-center justify-center rounded-full p-2 text-white">
                    <UIcon :name="icon" class="size-5 md:size-8" />
                </div>
                <h1 class="leading-none font-semibold">{{ title }}</h1>
            </div>
        </BaseWidthContainer>
    </section>
</template>
